import { MouseEvent, useCallback, useContext } from 'react';
import { CartContext } from 'context-providers/cart-provider';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
import type { Product } from 'entities/product';

type UseAddToCartCtaArgs = {
  product?: Product;
  // Analytics label passed to buttonEventHandler.
  buttonName?: string;
  // Cart sku, when it isn't the product's own code (e.g. partner plans).
  sku?: string;
};

type AddToCartCta = {
  productId?: string | null;
  cartHref: string;
  handleAddToCart: (event: MouseEvent<HTMLAnchorElement>) => void;
};

// Shared add-to-cart action for PDP buy boxes: opens the cart drawer (falling
// back to the /cart href) and fires the analytics event. The CMS-driven
// presentation (label, price, blurb, reassurance list) stays at the call site.
export const useAddToCartCta = ({
  product,
  buttonName,
  sku,
}: UseAddToCartCtaArgs): AddToCartCta => {
  const { openCart } = useContext(CartContext);
  const cartSku = sku ?? product?.code;

  const handleAddToCart = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      buttonEventHandler(buttonName, 'PDP');
      if (cartSku) openCart({ skus: [cartSku] });
    },
    [buttonName, cartSku, openCart],
  );

  return {
    productId: product?.shopifyData?.productID,
    cartHref: `/cart?cart=${cartSku}`,
    handleAddToCart,
  };
};
